import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription
} from './ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { RefreshCw, AlertCircle, ChevronLeft, ChevronRight, Search, ArrowUp, ArrowDown } from 'lucide-react';
import { buildApiUrl } from '../config/api';

interface ClusterValue {
  id: number;
  value: string;
  tracked_at: string;
  event_name: string;
  event_id: number;
}

interface ClusterInfo {
  id: number;
  label: string;
  key: string;
  size: number;
  event_name: string;
}

interface Pagination {
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

interface ClusterValuesSheetProps {
  isOpen: boolean;
  onClose: () => void;
  clusterId: number | null;
  projectId: number | null;
  token: string;
}

const ClusterValuesSheet: React.FC<ClusterValuesSheetProps> = ({
  isOpen,
  onClose,
  clusterId,
  projectId,
  token
}) => {
  // Add animation and line-clamp styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fade-in {
        from {
          opacity: 0;
          transform: translateY(10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .animate-fade-in {
        animation: fade-in 0.3s ease-out;
      }
      .line-clamp-2 {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        line-height: 1.4;
        max-height: 2.8em;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Feed state
  const [values, setValues] = useState<ClusterValue[]>([]);
  const [cluster, setCluster] = useState<ClusterInfo | null>(null);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [searchLoading, setSearchLoading] = useState(false);

  const fetchClusterValues = async (page: number = 1, search?: string, sort?: 'asc' | 'desc') => {
    if (!clusterId || !projectId) return;

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '50',
        sort_order: sort || sortOrder
      });

      if (search || searchTerm) {
        params.append('search', search || searchTerm);
      }

      const response = await fetch(
        buildApiUrl(`api/v1/projects/${projectId}/clusters/${clusterId}/values?${params}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch cluster values');
      }

      const data = await response.json();
      // Clear values first to trigger animations
      setValues([]);
      setTimeout(() => {
        setValues(data.values || []);
      }, 50);
      setCluster(data.cluster);
      setPagination(data.pagination);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching cluster values:', error);
      setError('Failed to load cluster values');
      setValues([]);
      setCluster(null);
      setPagination(null);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  // Simple debounce function
  function debounce(func: Function, wait: number) {
    let timeout: NodeJS.Timeout;
    return function executedFunction(...args: any[]) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      setSearchLoading(true);
      fetchClusterValues(1, searchValue);
    }, 500),
    [clusterId, projectId, token, sortOrder]
  );

  useEffect(() => {
    if (isOpen && clusterId && projectId) {
      fetchClusterValues(1);
    } else if (!isOpen) {
      // Reset state when sheet closes
      setValues([]);
      setCluster(null);
      setPagination(null);
      setSearchTerm('');
      setCurrentPage(1);
      setError(null);
    }
  }, [isOpen, clusterId, projectId, token]);

  useEffect(() => {
    if (searchTerm) debouncedSearch(searchTerm);
    else if (searchTerm === '') {
      setSearchLoading(true);
      fetchClusterValues(1, '');
    }
  }, [searchTerm, debouncedSearch]);

  const handlePageChange = (p: number) =>
    pagination && p >= 1 && p <= pagination.total_pages && fetchClusterValues(p);

  const handleRefresh = () => fetchClusterValues(currentPage);

  const handleSortChange = () => {
    const newOrder = sortOrder === 'desc' ? 'asc' : 'desc';
    setSortOrder(newOrder);
    fetchClusterValues(1, searchTerm, newOrder);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const shouldShowTooltip = (val: string) => val.length > 80;

  const formatNumber = (n: number) =>
    n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(1)}K` : n.toString();

  return (
    <TooltipProvider>
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent className="w-[800px] sm:max-w-[800px] flex flex-col h-full">
          <div className="flex flex-col flex-1 min-h-0">
            <SheetHeader className="flex-none pb-6">
              <SheetTitle className="text-xl font-bold">
                {cluster ? cluster.label : 'Loading...'}
              </SheetTitle>
              <SheetDescription asChild>
                <div>
                  {cluster && (
                    <div className="space-y-2 mt-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {cluster.event_name || 'Unknown'}
                        </Badge>
                        <Badge variant="secondary" className="font-sans text-xs">
                          {cluster.key}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {formatNumber(cluster.size || 0)} events
                        </Badge>
                      </div>
                    </div>
                  )}
                </div>
              </SheetDescription>
            </SheetHeader>

            {/* Feed Content */}
            <div className="flex-1 flex flex-col space-y-4 min-h-0">
              {/* Error */}
              {error && (
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center space-x-2 text-red-700">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">{error}</span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Search and Controls */}
              <div className="space-y-4 flex-none">
                <div className="flex items-center space-x-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search through all values..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="pl-10"
                    />
                    {searchLoading && (
                      <RefreshCw className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={loading}
                  >
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>

                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    {pagination && (
                      <div className="text-sm text-muted-foreground">
                        Showing {((pagination.page - 1) * pagination.per_page) + 1} to{' '}
                        {Math.min(pagination.page * pagination.per_page, pagination.total_count)} of{' '}
                        {pagination.total_count} values
                        {searchTerm && (
                          <span className="ml-2">
                            (filtered by "{searchTerm}")
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Values Table */}
              <div className="rounded-md border flex-1 min-h-0 flex flex-col overflow-hidden">
                <div className="overflow-y-auto flex-1">
                  <Table>
                    <TableHeader className="sticky top-0 bg-background">
                      <TableRow>
                        <TableHead className="w-[80%]">Value</TableHead>
                        <TableHead className="w-[20%]">
                          <button
                            onClick={handleSortChange}
                            className="flex items-center space-x-1 hover:text-foreground transition-colors cursor-pointer font-medium"
                          >
                            <span>Tracked At</span>
                            <div className="flex flex-col">
                              <ArrowUp
                                className={`h-3 w-3 transition-colors ${
                                  sortOrder === 'asc' ? 'text-foreground' : 'text-muted-foreground/50'
                                }`}
                              />
                              <ArrowDown
                                className={`h-3 w-3 -mt-1 transition-colors ${
                                  sortOrder === 'desc' ? 'text-foreground' : 'text-muted-foreground/50'
                                }`}
                              />
                            </div>
                          </button>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={2} className="text-center py-16">
                            <div className="flex flex-col items-center space-y-4">
                              <div className="relative">
                                <div className="w-16 h-16 border-4 border-blue-200 rounded-full animate-pulse"></div>
                                <div className="absolute inset-0 w-16 h-16 border-4 border-blue-500 rounded-full animate-spin border-t-transparent"></div>
                              </div>
                              <div className="text-sm font-medium">Loading cluster values...</div>
                              <div className="text-xs text-muted-foreground">
                                {searchTerm ? `Searching for "${searchTerm}"...` : 'Fetching data...'}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : values.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={2} className="text-center py-16">
                            <div className="flex flex-col items-center space-y-4">
                              <div className="w-16 h-16 rounded-full bg-muted/30 flex items-center justify-center">
                                <Search className="h-8 w-8 text-muted-foreground" />
                              </div>
                              <div className="text-sm font-medium">
                                {searchTerm ? 'No matching values found' : 'No values found'}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {searchTerm ? `No values match "${searchTerm}"` : 'This cluster has no values'}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        values.map((value, index) => (
                          <TableRow
                            key={value.id}
                            className="hover:bg-muted/50 opacity-0 animate-fade-in"
                            style={{
                              animationDelay: `${index * 50}ms`,
                              animationFillMode: 'forwards'
                            }}
                          >
                            <TableCell className="max-w-0">
                              {shouldShowTooltip(value.value) ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="font-sans text-xs break-words line-clamp-2 cursor-help">
                                      {value.value}
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-sm">
                                    <p className="break-words">{value.value}</p>
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <div className="font-sans text-xs break-words line-clamp-2">
                                  {value.value}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {format(new Date(value.tracked_at), 'MMM d, h:mm a')}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Pagination */}
              {pagination && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t flex-none">
                  <div className="text-sm text-muted-foreground">
                    Page {pagination.page} of {pagination.total_pages}
                    <span className="ml-2 text-xs">
                      ({pagination.total_count} total values)
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={!pagination.has_prev || loading}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!pagination.has_next || loading}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </TooltipProvider>
  );
};

export default ClusterValuesSheet;
